import { useRef, useEffect, useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6tu8vn6t0doov8ffkvg';

const hours = [
  { day: 'Monday – Friday', time: '9:00 AM – 6:00 PM' },
  { day: 'Saturday', time: '9:00 AM – 6:00 PM' },
  { day: 'Sunday', time: '1:00 PM – 6:00 PM' },
];

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (message.length > 500) return;
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setMessage('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#F8F6F1]">
      <div
        ref={ref}
        className={`max-w-7xl mx-auto px-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#B8963E] text-xs font-bold tracking-widest uppercase mb-3">Get In Touch · 문의하기</p>
          <h2 className="font-['Playfair_Display'] text-[#1A1A1A] text-3xl lg:text-4xl font-bold mb-4">
            Contact Shawn Kim
          </h2>
          <p className="text-[#666] text-sm max-w-lg mx-auto">
            Ready to buy, sell, or invest? Reach out for a free consultation. 무료 상담을 위해 연락 주세요.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Info Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Info */}
            <div className="bg-white rounded-lg p-6">
              <h3 className="font-['Playfair_Display'] text-[#1A1A1A] font-bold text-lg mb-5">Contact Info</h3>
              <div className="space-y-4">
                <a href="tel:4049340670" className="flex items-start gap-3 group cursor-pointer">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#B8963E]/10 rounded-md flex-shrink-0">
                    <i className="ri-phone-fill text-[#B8963E]"></i>
                  </div>
                  <div>
                    <div className="text-xs text-[#888] mb-0.5">Phone</div>
                    <div className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#B8963E] transition-colors">(404) 934-0670</div>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#B8963E]/10 rounded-md flex-shrink-0">
                    <i className="ri-map-pin-2-fill text-[#B8963E]"></i>
                  </div>
                  <div>
                    <div className="text-xs text-[#888] mb-0.5">Office Address</div>
                    <div className="text-sm text-[#1A1A1A]">3790 Satellite Blvd #102<br />Duluth, GA 30096</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-white rounded-lg p-6">
              <h3 className="font-['Playfair_Display'] text-[#1A1A1A] font-bold text-lg mb-5">
                <span className="flex items-center gap-2">
                  <i className="ri-time-fill text-[#B8963E]"></i>
                  Business Hours
                </span>
              </h3>
              <div className="space-y-3">
                {hours.map((h) => (
                  <div key={h.day} className="flex justify-between items-center text-sm">
                    <span className="text-[#555]">{h.day}</span>
                    <span className="font-medium text-[#1A1A1A]">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Google Map */}
            <div className="rounded-lg overflow-hidden h-48">
              <iframe
                title="김산 부동산 Shawn Kim Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3314.8!2d-84.0998!3d33.9701!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88f5a3cf7f0a0001%3A0x1234!2s3790+Satellite+Blvd+%23102%2C+Duluth%2C+GA+30096!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Form Column */}
          <div className="lg:col-span-3 bg-white rounded-lg p-8">
            <h3 className="font-['Playfair_Display'] text-[#1A1A1A] font-bold text-xl mb-6">
              Send Us a Message
            </h3>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-full mb-4">
                  <i className="ri-check-line text-emerald-500 text-3xl"></i>
                </div>
                <h4 className="font-['Playfair_Display'] text-[#1A1A1A] text-xl font-bold mb-2">Message Sent!</h4>
                <p className="text-[#666] text-sm mb-6">Thank you for reaching out. Shawn will contact you shortly.<br />연락주셔서 감사합니다.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="text-[#B8963E] text-sm font-semibold underline cursor-pointer"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-[#555] mb-1.5">Full Name *</label>
                    <input
                      name="name"
                      type="text"
                      required
                      placeholder="John / 홍길동"
                      className="w-full border border-[#E0D8CC] rounded-md px-4 py-2.5 text-sm text-[#1A1A1A] placeholder-[#BBB] focus:outline-none focus:border-[#B8963E] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#555] mb-1.5">Email *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="yourname@email.com"
                      className="w-full border border-[#E0D8CC] rounded-md px-4 py-2.5 text-sm text-[#1A1A1A] placeholder-[#BBB] focus:outline-none focus:border-[#B8963E] transition-colors"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-[#555] mb-1.5">Phone Number</label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="(404) 000-0000"
                      className="w-full border border-[#E0D8CC] rounded-md px-4 py-2.5 text-sm text-[#1A1A1A] placeholder-[#BBB] focus:outline-none focus:border-[#B8963E] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#555] mb-1.5">Service Needed</label>
                    <select
                      name="service_type"
                      className="w-full border border-[#E0D8CC] rounded-md px-4 py-2.5 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#B8963E] transition-colors bg-white"
                    >
                      <option value="">Select a service...</option>
                      <option value="Buying a Home">Buying a Home · 주택 구매</option>
                      <option value="Selling a Home">Selling a Home · 주택 판매</option>
                      <option value="Commercial Real Estate">Commercial Real Estate · 상업용 부동산</option>
                      <option value="Investment Property">Investment Property · 투자 부동산</option>
                      <option value="Relocation">Relocation · 이주 서비스</option>
                      <option value="Free Consultation">Free Consultation · 무료 상담</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#555] mb-1.5">
                    Message · 메시지
                    <span className="ml-2 font-normal text-[#AAA]">({message.length}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    maxLength={500}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us about your real estate needs... 부동산 관련 문의사항을 남겨주세요..."
                    className="w-full border border-[#E0D8CC] rounded-md px-4 py-2.5 text-sm text-[#1A1A1A] placeholder-[#BBB] focus:outline-none focus:border-[#B8963E] transition-colors resize-none"
                  />
                  {message.length > 500 && (
                    <p className="text-xs text-red-500 mt-1">Message cannot exceed 500 characters.</p>
                  )}
                </div>

                {status === 'error' && (
                  <p className="text-xs text-red-500">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending' || message.length > 500}
                  className="w-full bg-[#B8963E] hover:bg-[#A07830] disabled:opacity-60 text-white py-3.5 rounded-md text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {status === 'sending' ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                  ) : (
                    <><i className="ri-send-plane-fill"></i> Send Message · 보내기</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
